import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/hooks/useTheme";
import { AuthProvider, PrivateRoute, HomeRoute } from "@/lib/auth";
import { useDynamicFavicon } from "@/hooks/useDynamicFavicon";
import { loadChunkWithRetry } from "@/lib/lazyRetry";

// 2026-02 Phase F.3 — outage-hardening.  Global last-resort catch for
// chunk-load failures that survive `loadChunkWithRetry`'s in-process
// retry.  Scenario we're guarding against: user has a stale cached
// `index.html` (from a previous deploy) that references
// `static/js/main.<oldHash>.js` — the new pod has purged those hashed
// bundles, so BOTH import attempts 404.  Without this guard, React
// throws to nowhere and the user sees a white screen with cryptic
// console noise ("Loading chunk N failed").  With it, we do exactly
// one full-page reload (guarded by sessionStorage so we can't loop),
// which lets the browser re-fetch a fresh `index.html` with the
// correct hashes.
//
// Cheap: zero cost until an error fires.  Safe: single-shot per tab
// session — if the reload doesn't fix it (e.g. the network really is
// down), we let the error propagate normally.
if (typeof window !== "undefined") {
  const CHUNK_ERR_RE = /(ChunkLoadError|Loading chunk .* failed|Failed to fetch dynamically imported module|Importing a module script failed)/i;
  const SENTINEL = "gf.chunk-reload-attempted";
  const tryReloadOnce = (err) => {
    const msg = err && (err.message || err.reason?.message || String(err));
    if (!msg || !CHUNK_ERR_RE.test(msg)) return false;
    try {
      if (sessionStorage.getItem(SENTINEL) === "1") return false;
      sessionStorage.setItem(SENTINEL, "1");
    } catch (_e) { /* incognito / sandboxed — skip guard */ }
    // Wipe the SW cached shell first so the reload can't re-serve the
    // stale one.  Best-effort — proceed even if it errors.
    if ("caches" in window) {
      caches.keys()
        .then((keys) => Promise.all(keys.filter((k) => k.startsWith("gc-core-")).map((k) => caches.delete(k))))
        .finally(() => window.location.reload());
    } else {
      window.location.reload();
    }
    return true;
  };
  window.addEventListener("error", (e) => { tryReloadOnce(e.error || e); });
  window.addEventListener("unhandledrejection", (e) => { tryReloadOnce(e.reason || e); });
  // Clear the sentinel on a fully-successful load so the NEXT time this
  // tab hits a chunk error (weeks later, after another deploy) it can
  // reload again.  Fires on window `load` — which only runs when the
  // main bundle has actually parsed and executed.
  window.addEventListener("load", () => {
    try { sessionStorage.removeItem(SENTINEL); } catch (_e) { /* noop */ }
  });
}

// 2026-02 Phase B3 — route-level code splitting.  Every page is a
// separate chunk so the initial `main.js` bundle only carries the
// shell (App/AuthProvider/theme/router/toaster).  The first-page
// chunk loads in parallel with the initial API round-trips (auth/me
// + branding meta), so the wall-clock time-to-interactive on Dashboard
// is unchanged, while the OTHER routes (Editor, Settings, Share,
// PublicHome, Signup) no longer sit in the critical path.
// Wrapped in `loadChunkWithRetry` so a cold-load ChunkLoadError (rare
// on flaky mobile / after a hot-deploy) auto-retries once before
// throwing.
const Dashboard = lazy(() => loadChunkWithRetry(() => import("@/pages/Dashboard")));
const Editor = lazy(() => loadChunkWithRetry(() => import("@/pages/Editor")));
const SharePage = lazy(() => loadChunkWithRetry(() => import("@/pages/SharePage")));
const Login = lazy(() => loadChunkWithRetry(() => import("@/pages/Login")));
const Signup = lazy(() => loadChunkWithRetry(() => import("@/pages/Signup")));
const Settings = lazy(() => loadChunkWithRetry(() => import("@/pages/Settings")));
const PublicHome = lazy(() => loadChunkWithRetry(() => import("@/pages/PublicHome")));
// 2026-02 Phase D1 — owner-mediated password reset pages.
const ForgotPassword = lazy(() => loadChunkWithRetry(() => import("@/pages/ForgotPassword")));
const ResetPassword = lazy(() => loadChunkWithRetry(() => import("@/pages/ResetPassword")));
const ForcedPasswordChange = lazy(() => loadChunkWithRetry(() => import("@/pages/ForcedPasswordChange")));

// Neutral full-page loader — no branded flash since the AuthProvider
// already shows the branded shell (logo + subtle spinner) while
// /me resolves.  Sub-route swaps take <200 ms on a warm cache and
// the neutral background prevents a "double loading" flicker.
function RouteFallback() {
  return <div aria-hidden className="min-h-screen bg-slate-50 dark:bg-slate-950" />;
}

function App() {
  useDynamicFavicon();
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/share/:token" element={<SharePage />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/change-password-required"
                  element={
                    <PrivateRoute>
                      <ForcedPasswordChange />
                    </PrivateRoute>
                  }
                />
                {/* Root is auth-aware: unauthenticated visitors get the
                    public read-only home; logged-in users get the full
                    Dashboard. HomeRoute never redirects — it just picks
                    the right subtree once /me resolves. */}
                <Route
                  path="/"
                  element={
                    <HomeRoute
                      authed={<Dashboard />}
                      unauthed={<PublicHome />}
                    />
                  }
                />
                <Route
                  path="/projects/:id"
                  element={
                    <PrivateRoute>
                      <Editor />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <PrivateRoute>
                      <Settings />
                    </PrivateRoute>
                  }
                />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="bottom-right" richColors closeButton />
      </div>
    </ThemeProvider>
  );
}

export default App;
